import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

// SERVICES
import { GetDIDsService } from '../getDIDs/get-dids.service';
import { PackagePriceService } from '../package-price/package-price.service';
import { GoogleTagManagerService } from '../google-tag-manager/google-tag-manager.service';
import { ConversionApiService } from '../conversion-api/conversion-api.service';
import { CountriesService } from '../countries/countries.service';

// INTERFACES
import { IDids } from '../../models/dids.model';
import { IPackagePrice } from '../../models/package-price.model';
import { IDidCountries, IPackageCountries } from '../../models/countries.model';
import { SubmitOrderService } from '../submit-order/submit-order.service';

export interface IChooseNumberPageData {
  didCountry: IDidCountries;
  packageCountry: IPackageCountries;
  didNumbers: IDids[];
  packagePrice: IPackagePrice | null;
  priceInteger: string | null;
  priceDecimal: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ChooseNumberService {
  private getDIDsService = inject(GetDIDsService);
  private packagePriceService = inject(PackagePriceService);
  private gtmService = inject(GoogleTagManagerService);
  private conversionApiService = inject(ConversionApiService);
  private countriesService = inject(CountriesService);
  private submitOrderService = inject(SubmitOrderService);

  async getPageData(
    didCountryName: string,
    packageCountryName: string
  ): Promise<IChooseNumberPageData> {
    // 1. Get Countries Data
    const [didCountryRes, packageCountryRes] = await Promise.all([
      this.countriesService.getDidCountryByName<IDidCountries>(didCountryName),
      this.countriesService.getPackageCountryByName<IPackageCountries>(packageCountryName),
    ]);

    if (
      !didCountryRes.isSuccessful ||
      !packageCountryRes.isSuccessful ||
      !didCountryRes.result ||
      !packageCountryRes.result
    ) {
      throw new Error('Country data could not be fetched.');
    }

    const didCountry = didCountryRes.result;
    const packageCountry = packageCountryRes.result;
    
    const orderData = {
      didCountry: didCountry,
      packageCountry: packageCountry,
    };
    this.submitOrderService.setOrderData(orderData);

    // 2. Get DIDs and Price Data
    const [didsRes, priceRes] = await Promise.all([
      this.getDIDsService.getAvailableDIDs<IDids[]>(didCountry.countryID),
      this.packagePriceService.getPackagePrice<IPackagePrice>({
        packageId: Number(packageCountry.packageId),
        countryId: didCountry.countryID,
        didId: null,
        price: 0,
      }),
    ]);

    // 3. Process DIDs for GTM
    if (didsRes.isSuccessful && didsRes.result) {
      const allDids = didsRes.result.flatMap(item => item.dids);
      this.gtmService.viewItem(
        allDids,
        packageCountry.packageId.toString(),
        packageCountryName
      );
      this.conversionApiService.trackEvent('ViewContent');
    }

    // 4. Process Price
    let priceInteger: string | null = null;
    let priceDecimal: string | null = null;
    let packagePrice: IPackagePrice | null = null;

    if (priceRes.isSuccessful && priceRes.result) {
      packagePrice = priceRes.result;
      let minPrice = packagePrice.price;

      if (
        Array.isArray(packagePrice.discounts) &&
        packagePrice.discounts.length > 0
      ) {
        const discountPrices = packagePrice.discounts.map(d => d.price);
        minPrice = Math.min(minPrice, ...discountPrices);
      }

      const priceParts = minPrice.toFixed(2).split('.');
      priceInteger = priceParts[0];
      priceDecimal = priceParts[1];
    }

    // 5. Return all data
    return {
      didCountry,
      packageCountry,
      didNumbers: didsRes.result || [],
      packagePrice: packagePrice,
      priceInteger: priceInteger,
      priceDecimal: priceDecimal,
    };
  }
}
